import {Injectable} from "@angular/core";
import {OptionsChainService} from "../../../option-chains.service";
import {HedgePositionModel} from "./hedge-position.model";
import {isVoid} from "../../../utils";
import {HedgePosition} from "../../data-model/hedge-position";
import {MessageBusService} from "../../../message-bus.service";
import {ServiceConfiguration} from "../../../adjustment-pricing-grid/services/ServiceConfiguration";
import {ApgPortfolio} from "../../../adjustment-pricing-grid/model/ApgPortfolio";
import {UserSettingsService} from "../../../user-settings.service";
import {ApgDataService} from "../../../adjustment-pricing-grid/services/apg-data.service";
import {SessionService} from "../../../authentication/session-service.service";
import {ShellClientService} from '../../../shell-communication/shell-client.service';
import {CompleteAvailableAdjustment, AdjustmentAcquisitionReference} from '../../copilot/available-adjustment-operations';

const StorageKey = 'hg.hedge-positions';
const AfterStateKey = 'hg.after-state'
const AfterStateFilterKey = 'hg.after-state.filter'

export interface HgAfterStateFilter  {
    excluded: string[];
    removed: string[];
    applied: string[];
}

@Injectable()
export class HedgePositionsService {

    constructor(
        private readonly _optionChainsService: OptionsChainService,
        private readonly _messageBus: MessageBusService,
        private readonly _apgDataService: ApgDataService,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _shell: ShellClientService,
    ) {
    }

    async getPositionModels(portfolio: ApgPortfolio): Promise<HedgePositionModel[]> {

        let models = [];

        try {
            const dtos = await this.getHedgePositions(portfolio);
            models = await this.convertToModels(dtos);
        } catch (e) {
            console.error(e);
        }

        return models;
    }

    /**
     * Returns all hedge positions for a portfolio — both the base underlying
     * and "others" (e.g., ES contracts). Each position carries its own `asset` field.
     */
    async getHedgePositions(portfolio: ApgPortfolio): Promise<HedgePosition[]> {
        const basePositions = await this.getBaseHedgePositions(portfolio);
        const otherPositions = this.getOtherHedgePositions(portfolio);
        return [...basePositions, ...otherPositions];
    }

    /**
     * The two user-settings keys the saved hedge book lives under (base underlying + "others"),
     * exactly as getHedgePositions reads them. CR-20260916-004: Maven watches the shell's
     * settings echo for these keys to tell the trader the hedges changed since his run.
     */
    async getHedgePositionsKeys(portfolio: ApgPortfolio): Promise<string[]> {
        const baseKey = await this.makeStorageKey(portfolio);
        return [baseKey, this.makeOthersStorageKey(portfolio)];
    }

    private async getBaseHedgePositions(portfolio: ApgPortfolio): Promise<HedgePosition[]> {
        const key = await this.makeStorageKey(portfolio);

        const positions = this._userSettingsService.getValue<HedgePosition[]>(
            key,
            portfolio?.userId
        ) || [];

        return positions;
    }

    private getOtherHedgePositions(portfolio: ApgPortfolio): HedgePosition[] {
        const key = this.makeOthersStorageKey(portfolio);

        const positions = this._userSettingsService.getValue<HedgePosition[]>(
            key,
            portfolio?.userId
        ) || [];

        return positions;
    }

    saveAfterState(positions: HedgePosition[], slot: 'first' | 'second', portfolio: ApgPortfolio) {

        if (isVoid(portfolio)) {
            return;
        }

        const key = this.makeAfterStateStorageKey(portfolio, slot);

        this._userSettingsService.setValue(key, positions, portfolio?.userId);

        this._messageBus.publishAsync({
            topic: 'Hg.AfterStateUpdated',
            payload: {
                portfolio
            }
        });
    }

    getAfterState(slot: 'first' | 'second', portfolio: ApgPortfolio): HedgePosition[] {

        const key = this.makeAfterStateStorageKey(portfolio, slot);

        const portfolioUserState = this._userSettingsService.getValue<HedgePosition[]>(
            key,
            portfolio.userId
        );

        return portfolioUserState;
    }

    saveAfterStateFilter(filterData: HgAfterStateFilter, portfolio: ApgPortfolio, slot: 'first' | 'second') {
        if (isVoid(portfolio)) {
            return;
        }

        const key = this.makeAfterStateFilterKey(portfolio, slot);

        const _ = this._userSettingsService.setValue(key, filterData, portfolio?.userId);
    }

    getAfterStateFilter(portfolio: ApgPortfolio, slot: 'first' | 'second'): HgAfterStateFilter {

        if (isVoid(portfolio)) {
            return;
        }

        const key = this.makeAfterStateFilterKey(portfolio, slot);

        const filterState = this._userSettingsService.getValue<HgAfterStateFilter>(
            key,
            portfolio.userId
        );

        return filterState;
    }

    clearAfterState(portfolio: ApgPortfolio, slot?: 'first' | 'second') {

        const slots = isVoid(slot)
            ? ['first', 'second']
            : [slot];

        slots.forEach(slot => {
            const key = this.makeAfterStateStorageKey(portfolio, slot);
            this._userSettingsService.deleteValue(key, portfolio.userId);

            const filterKey = this.makeAfterStateFilterKey(portfolio, slot);
            this._userSettingsService.deleteValue(filterKey, portfolio.userId);
        });

        this._messageBus.publishAsync({
            topic: 'Hg.AfterStateUpdated',
            payload: {
                portfolio
            }
        });
    }

    adjustAfterState(positions: HedgePosition[], portfolio: ApgPortfolio, slot?: 'first' | 'second') {

        if (isVoid(portfolio)) {
            return;
        }

        const key = this.makeAfterStateStorageKey(portfolio, slot);

        this._userSettingsService.setValue(key, positions, portfolio?.userId)
            .then(() => {
                this._messageBus.publishAsync({
                    topic: 'Hg.AfterStateUpdated',
                    payload: {
                        portfolio
                    }
                });
            });
    }


    async convertToModels(positions: HedgePosition[]): Promise<HedgePositionModel[]> {
        const promises =
            positions.map(async x => {
                const dto = await HedgePositionModel.fromDto(x, this._optionChainsService);
                return dto;
            });

        const hedgePositionModels
            = await Promise.all(promises);

        return hedgePositionModels;
    }

    async saveHedgePositions(currentHedgePositions: HedgePosition[], portfolio: ApgPortfolio) {
        this._userSettingsService.requireOwnSettings(portfolio?.userId);
        const baseKey = await this.makeStorageKey(portfolio);
        const othersKey = this.makeOthersStorageKey(portfolio);

        const baseUnderlying = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);

        // Partition positions: base underlying vs. others
        const basePositions = currentHedgePositions.filter(
            p => isVoid(p.asset) || p.asset === baseUnderlying
        );
        const otherPositions = currentHedgePositions.filter(
            p => !isVoid(p.asset) && p.asset !== baseUnderlying
        );

        // Keep the original settings operations. Wait for both and propagate any failure.
        await this._userSettingsService.setValueConfirmed(baseKey, basePositions);
        if (otherPositions.length > 0) {
            await this._userSettingsService.setValueConfirmed(othersKey, otherPositions);
        } else {
            await this._userSettingsService.deleteValueConfirmed(othersKey);
        }

        this._messageBus.publish({
            topic: 'Hg.HedgePositionsChanged',
            payload: {
                portfolio
            }
        });
    }


    async completeAcquiredAdjustments(portfolio: ApgPortfolio, references: AdjustmentAcquisitionReference[]): Promise<void> {
        if (!references.length) { return; }
        await this._shell.processCommand(new CompleteAvailableAdjustment(portfolio.id, references));
    }

    private async makeStorageKey(portfolio: ApgPortfolio): Promise<string> {

        const ul = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);

        let rootKey = StorageKey;

        let key = `${rootKey}.${portfolio.id}.${ul}`;

        return key;
    }

    private makeOthersStorageKey(portfolio: ApgPortfolio): string {
        return `${StorageKey}.${portfolio.id}.others`;
    }

    private makeAfterStateStorageKey(portfolio: ApgPortfolio, slot: string): string {

        let rootKey = AfterStateKey;

        let key = `${rootKey}.${portfolio.id}.${slot}`;

        return key;
    }

    private makeAfterStateFilterKey(portfolio: ApgPortfolio, slot: string): string {
        let rootKey = AfterStateFilterKey;

        let key = `${rootKey}.${portfolio.id}.${slot}`;

        return key;
    }

}
